import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../theme';
import Icons from './Icons';
import DBBrowser from './DBBrowser';
import GenerateSQL from './GenerateSQL';

// ---------------------------------------------------------------------------
// RightPanel — tabbed panel hosting DB Browser (schema) and Generate SQL.
// Owns panel width + drag-resize; content components are stateless about size.
// ---------------------------------------------------------------------------
export default function RightPanel({
  activeTab,
  onTabChange,
  onClose,
  // DB Browser props
  schema,
  schemaLoading,
  onRefresh,
  onTableAction,
  // GenerateSQL props
  aiQueries,
  onGenerate,
  onSave,
  onDelete,
  onSelect,
  currentSql,
  currentAiPrompt,
  onRefine,
}) {
  const [panelWidth, setPanelWidth] = useState(320);
  const [dragging, setDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      const delta = dragStartX.current - e.clientX;
      const newWidth = Math.max(240, Math.min(640, dragStartWidth.current + delta));
      setPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      setDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  const handleDragStart = (e) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    setDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div style={{
      width: `${panelWidth}px`, flexShrink: 0,
      display: 'flex', overflow: 'hidden',
      background: theme.surface,
    }}>
      {/* Left drag handle */}
      <div
        onMouseDown={handleDragStart}
        style={{
          width: '5px', flexShrink: 0, cursor: 'col-resize',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: dragging ? `${theme.accent}22` : 'transparent',
          borderLeft: `1px solid ${theme.border}`,
          transition: dragging ? 'none' : 'background 0.15s',
        }}
        onMouseEnter={(e) => { if (!dragging) e.currentTarget.style.background = `${theme.accent}11`; }}
        onMouseLeave={(e) => { if (!dragging) e.currentTarget.style.background = 'transparent'; }}
      />

      {/* Panel content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Tab content */}
        {activeTab === 'schema' && (
          <DBBrowser
            schema={schema}
            loading={schemaLoading}
            onRefresh={onRefresh}
            onClose={onClose}
            onTableAction={onTableAction}
          />
        )}
        {activeTab === 'generate' && (
          <>
            {/* Generate header with close */}
            <div style={{
              padding: '10px 12px', borderBottom: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <span style={{ fontWeight: 600, fontSize: '13px' }}>Generate SQL</span>
              <button onClick={onClose}
                style={{ background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                title="Close panel"
              >
                <Icons.X />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '10px' }}>
              <GenerateSQL
                schema={schema}
                onGenerate={onGenerate}
                savedItems={aiQueries}
                onSave={onSave}
                onDelete={onDelete}
                onSelect={onSelect}
                currentSql={currentSql}
                currentAiPrompt={currentAiPrompt}
                onRefine={onRefine}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

